import { useState, useEffect, useCallback } from "react";
import { Link } from "@/lib/router-compat";
import { X, Settings2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { syncUtmCookieWithConsent } from "@/lib/utm";

const COOKIE_KEY = "gohub_cookie_consent";

export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  functionality: boolean;
  marketing: boolean;
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  functionality: false,
  marketing: false,
};


/* ── helpers ─────────────────────────────────────────────── */

function readConsent(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentPreferences;
  } catch {
    return null;
  }
}

function writeConsent(prefs: ConsentPreferences) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs));
}

/**
 * Push consent state to the GTM dataLayer using Google Consent Mode v2.
 * GTM reads these and gates tags accordingly.
 */
function pushConsentToDataLayer(prefs: ConsentPreferences) {
  window.dataLayer = window.dataLayer || [];

  // gtag helper expected by Consent Mode v2
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
  }

  gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: prefs.functionality || prefs.necessary ? "granted" : "denied",
    personalization_storage: prefs.functionality ? "granted" : "denied",
    security_storage: "granted",
  });

  // Also fire a custom event so GTM triggers can react
  window.dataLayer.push({
    event: "cookie_consent_update",
    cookie_consent: prefs,
  });

  // Load or remove HubSpot tracking script based on consent
  manageHubSpotScript(prefs);

  // Persist or delete the UTM attribution cookie based on marketing consent
  syncUtmCookieWithConsent(prefs);
}

const HS_SCRIPT_ID = "hs-script-loader";

function manageHubSpotScript(prefs: ConsentPreferences) {
  const shouldLoad = prefs.analytics || prefs.marketing;
  const existing = document.getElementById(HS_SCRIPT_ID);

  if (shouldLoad && !existing) {
    const script = document.createElement("script");
    script.id = HS_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.defer = true;
    script.src = "https://js-eu1.hs-scripts.com/147645955.js";
    document.head.appendChild(script);
  } else if (!shouldLoad && existing) {
    existing.remove();
  }
}

/* ── types ────────────────────────────────────────────────── */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/* ── component ───────────────────────────────────────────── */

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setPrefs(stored);
      pushConsentToDataLayer(stored);
      return;
    }

    // Global Privacy Control: auto opt out without showing the banner.
    const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
    if (gpc) {
      const optedOut: ConsentPreferences = {
        necessary: true,
        analytics: false,
        functionality: false,
        marketing: false,
      };
      writeConsent(optedOut);
      pushConsentToDataLayer(optedOut);
      setPrefs(optedOut);
      return;
    }

    setVisible(true);
  }, []);

  const accept = useCallback((overrides: Partial<ConsentPreferences> = {}) => {
    const final: ConsentPreferences = { ...prefs, ...overrides, necessary: true };
    writeConsent(final);
    pushConsentToDataLayer(final);
    setPrefs(final);
    setVisible(false);
    setShowDetails(false);
  }, [prefs]);

  const acceptAll = () => accept({ analytics: true, functionality: true, marketing: true });
  const rejectAll = () => accept({ analytics: false, functionality: false, marketing: false });
  const savePreferences = () => accept(prefs);

  // Expose a way for Footer "Cookie Settings" button to re-open the banner
  useEffect(() => {
    const open = () => {
      setVisible(true);
      setShowDetails(true);
    };
    // Handle a request fired before this (lazy loaded) component mounted.
    if ((window as Window & { __gohubOpenCookieSettings?: boolean }).__gohubOpenCookieSettings) {
      (window as Window & { __gohubOpenCookieSettings?: boolean }).__gohubOpenCookieSettings = false;
      open();
    }
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);


  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6">
      <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-2xl">
        {/* ── Header ─────────────────────────────────── */}
        <div className="mb-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-primary" />
            <h3 className="text-base font-bold text-foreground">Cookie Settings</h3>
          </div>
          <button
            onClick={rejectAll}
            aria-label="Close"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Description ────────────────────────────── */}
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          We use cookies to improve your experience, analyse site traffic, and personalise content.
          Read our{" "}
          <Link to="/cookie-policy" className="text-primary underline hover:text-primary/80">
            Cookie Policy
          </Link>{" "}
          to learn more.
        </p>

        {/* ── Detailed preferences (togglable) ──────── */}
        {showDetails && (
          <div className="mb-4 space-y-3 rounded-xl bg-muted p-4">
            {/* Necessary */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Necessary</p>
                <p className="text-xs text-muted-foreground">Required for the site to function</p>
              </div>
              <Switch checked disabled className="opacity-60" />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Analytics</p>
                <p className="text-xs text-muted-foreground">Help us understand site usage</p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
            </div>

            {/* Functionality */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Functionality</p>
                <p className="text-xs text-muted-foreground">Remember preferences such as language or region</p>
              </div>
              <Switch
                checked={prefs.functionality}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, functionality: v }))}
              />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Marketing</p>
                <p className="text-xs text-muted-foreground">Relevant ads &amp; campaigns</p>
              </div>
              <Switch
                checked={prefs.marketing}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>
          </div>
        )}

        {/* ── Actions ────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2">
          {!showDetails && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setShowDetails(true)}
            >
              <Settings2 size={14} className="mr-1" />
              Manage Preferences
            </Button>
          )}

          {showDetails && (
            <Button
              size="sm"
              variant="outline"
              className="border-border text-foreground hover:bg-muted"
              onClick={savePreferences}
            >
              Save Preferences
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-border text-foreground hover:bg-muted"
              onClick={rejectAll}
            >
              Reject All
            </Button>
            <Button size="sm" onClick={acceptAll}>
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
